import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useForm } from "react-hook-form";

import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useToasts } from "@/hooks";
import { useOrderItemsPreMedia } from "@/oms/hooks/queries/useOrderItemPreMedia";
import { type ModalPreMediaPropositionFileFormProps } from "./ModalPreMediaPropositionFileForm.types";
import { ModalPreMediaPropositionFileFormDropfile } from "./ModalPreMediaPropositionFileFormDropfile";
import {
  type PreMediaPropositionFileFormSchema,
  preMediaPropositionFileFormSchema,
} from "./PreMediaPropositionFileForm.schema";

export const ModalPreMediaPropositionFileForm = ({
  isOpen,
  onCloseModal,
  orderItem,
}: ModalPreMediaPropositionFileFormProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const methods = useForm<PreMediaPropositionFileFormSchema>({
    resolver: zodResolver(preMediaPropositionFileFormSchema),
  });

  const { useAddSandboxItemPreMediaPropositionFile } = useOrderItemsPreMedia();
  const {
    mutateAsync: addSandbxItemPreMediaPropositionFile,
    isPending: isAddingSandboxItemPreMediaPropositionFile,
  } = useAddSandboxItemPreMediaPropositionFile();

  const handleAddPreMediaPropositionFile = async () => {
    if (fileSignedId) {
      try {
        const res = await addSandbxItemPreMediaPropositionFile({
          orderId: orderItem.orderId,
          orderItemId: orderItem.id,
          command: {
            fileSignedId,
          },
        });

        if (res.status === 201) {
          add({
            state: "success",
            title: t("toast.type.success"),
          });
          onCloseModal();
        } else {
          add({
            state: "error",
            title: t("toast.generic.error"),
          });
        }
      } catch {
        add({
          state: "error",
          title: t("toast.generic.error"),
        });
      }
    } else {
      add({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  const fileSignedId = methods.watch("fileSignedId");

  return (
    <FileContextProvider>
      <FileContext.Consumer>
        {(ctx) => (
          <KdsModal
            isOpen={isOpen}
            callbackOnClose={onCloseModal}
            dialogProps={{
              dialogHeaderProps: {
                title: t(
                  "order.myOrdersItemDetails.preMedia.addPreMediaPropositionFile"
                ),
              },
              content: (
                <ModalPreMediaPropositionFileFormDropfile methods={methods} />
              ),
              dialogFooterProps: {
                buttonProps: [
                  {
                    variant: "ghost",
                    color: "neutral",
                    children: t("buttons.cancel"),
                    onPress: onCloseModal,
                  },
                  {
                    children: t(
                      "order.myOrdersItemDetails.preMedia.integrateToZiflow"
                    ),
                    isLoading:
                      ctx.isUploading ||
                      isAddingSandboxItemPreMediaPropositionFile,
                    disabled:
                      !fileSignedId ||
                      ctx.isUploading ||
                      isAddingSandboxItemPreMediaPropositionFile,
                    type: "submit",
                    dataCy: "submitPreMediaPropositionFileButton",
                    onPress: () => void handleAddPreMediaPropositionFile(),
                  },
                ],
              },
            }}
          />
        )}
      </FileContext.Consumer>
    </FileContextProvider>
  );
};
